import { stateError, stateInitial, stateLoading } from "@shared/models/state";
import type { TwinManagementTypes } from "@inctec/smx-ui-client";
import {
  PropertyConstraint,
  type PropertyCreate,
  type TwinRequest,
  type TwinResponse,
  createProperty,
  createTwin,
  getPropertyConstraints,
  getTwin,
  searchTwins,
  patchTwin,
  TwinQuery,
  TwinPatch,
  patchProperty,
  deleteProperty,
  TwinRelationResponse,
  TwinRelationsQuery,
  getTwinRelations,
  TwinRelationRequest,
  postTwinRelation,
  deleteTwinRelation,
  TwinTemplateRelations,
  getTwinTemplateRelations,
  createTwinWithProperties,
} from "@shared/services/twinManagement";
import { defineStore } from "pinia";
import { computed, reactive, ref, watch } from "vue";
import { stateLoaded, LoadState, SearchResponseState } from "../models/state";
import { PaginationQuery } from "@shared/services/common";
import {
  TwinPropertyValues,
  ValueListItem,
  ValueListMetaData,
  addTwinPropertyValues,
  addValueListItem,
  deleteValueList,
  deleteValueListItem,
  getValueListItems,
  getValueListsMetaData,
  patchValuesList,
  postValueList,
} from "@shared/services/twinData";
import _ from "lodash";

// Central state for twin templates, lists, relations, value lists, and single
// twins. Use this store when data must be shared or refreshed across views.
// New single-twin consumers must use the UUID-keyed twinLoadingStates cache via
// getTwinLoadingState and loadTwinCached to prevent concurrent views overwriting
// each other's state.
export const useTwinStore = defineStore("twins", () => {
  const templatesLoadingState = ref<
    SearchResponseState<TwinResponse[], PaginationQuery>
  >({
    ...stateInitial,
    result: [],
  });

  // NOTE: we load all templates at once, since this data is relevant in multiple situations.
  // Backend required paging, so this will break if there are more than 1000 templates
  async function getTwinTemplates(keepCache = true) {
    const params = {
      page: 1,
      pageSize: 1000,
    };

    if (
      (templatesLoadingState.value.loaded ||
        templatesLoadingState.value.loading) &&
      keepCache
    ) {
      return;
    }

    templatesLoadingState.value = {
      ...stateLoading,
      query: params,
      result: templatesLoadingState.value.result,
    };

    const response = await searchTwins(params, { isTemplate: true }).catch(
      (e) => {
        templatesLoadingState.value = {
          ...templatesLoadingState.value,
          ...stateError,
          query: undefined,
        };
        throw e;
      },
    );

    templatesLoadingState.value = {
      ...stateLoaded,
      query: params,
      ...response.data,
    };
    return response.data;
  }

  async function createTwinTemplate(template: TwinRequest) {
    await createTwin(template);
    await getTwinTemplates(false);
  }

  async function createTemplateProperty(property: PropertyCreate) {
    await createProperty(property);
    // reload asset twin if loaded
    const twinId = templateLoadingState.value.result?.uuid;
    if (twinId) {
      await loadTemplate(twinId);
    }
  }

  async function deleteTemplateProperty(propertyId: string) {
    await deleteProperty(propertyId);
    // reload asset twin if loaded
    const twinId = templateLoadingState.value.result?.uuid;
    if (twinId) {
      await loadTemplate(twinId);
    }
  }

  function getTwinSubType(
    asset: Pick<TwinManagementTypes.TwinResponse, "type">,
  ) {
    const hierarchy = asset.type.split(".");
    return hierarchy.length >= 2 ? hierarchy[1] : undefined;
  }

  const twinTypes = computed(() => {
    return (
      Array.from(
        new Set(
          templatesLoadingState.value?.result
            ?.filter((item) => item.active)
            .map((item) => getTwinSubType(item))
            .filter((item) => item !== undefined),
        ),
      ) ?? []
    );
  });

  const templateByPropertyUuid = computed(() => {
    const map = new Map<string, string>();
    for (const template of templatesLoadingState.value.result ?? []) {
      for (const prop of template.properties ?? []) {
        map.set(prop.uuid, template.type);
      }
    }
    return map;
  });

  // Twin tables is used to load table data for twins, this has the advantage of global data reloading on updates and caching of queries for each table
  const twinTables = ref(
    new Map<string, SearchResponseState<TwinResponse[], TwinQuery>>(),
  );

  async function loadTwinTable(
    id: string,
    { page, query }: { page: PaginationQuery; query: TwinQuery },
    keepCache?: boolean,
  ) {
    const current = twinTables.value.get(id);
    if (
      current &&
      keepCache &&
      (current.loading || current.loaded) &&
      _.isEqual(page, current.page) &&
      _.isEqual(query, current.query)
    ) {
      return;
    }
    twinTables.value.set(id, { ...current, ...stateLoading, query });
    const response = await searchTwins(page, query).catch((e) => {
      twinTables.value.set(id, { ...stateError, result: [], query });
      throw e;
    });

    twinTables.value.set(id, { ...stateLoaded, ...response.data, query });

    return response;
  }

  const mergedTwinTableData = computed(() =>
    Array.from(twinTables.value.values()).flatMap((item) => item.result),
  );

  async function reloadTwinTable(id: string) {
    const result = twinTables.value.get(id);
    if (
      result?.page !== undefined &&
      result.pageSize !== undefined &&
      result.query
    ) {
      await loadTwinTable(id, {
        page: { page: result.page, pageSize: result.pageSize },
        query: result.query,
      });
    }
  }

  const twinsLoadingState = ref<SearchResponseState<TwinResponse[], TwinQuery>>(
    {
      ...stateInitial,
      result: [],
    },
  );

  async function loadTwins(paging: PaginationQuery, query?: TwinQuery) {
    const page = {
      page: paging ? paging.page : 1,
      pageSize: paging ? paging.pageSize : 20,
    };

    const params = {
      isTemplate: false,
      showProperties: true,
      active: true,
      ...query,
    };

    twinsLoadingState.value = {
      ...twinsLoadingState.value,
      ...stateLoading,
      query: undefined,
    };

    const response = await searchTwins(page, params).catch((e) => {
      twinsLoadingState.value = {
        ...twinsLoadingState.value,
        ...stateError,
        query: query,
      };
      throw e;
    });

    twinsLoadingState.value = {
      ...stateLoaded,
      ...response.data,
      query: params,
    };
  }

  /**
   * Reload assets with the same query that has been run the last time, to preserve paging, sort etc.
   */
  async function reloadTwins() {
    if (
      twinsLoadingState.value.loaded &&
      twinsLoadingState.value.page !== undefined &&
      twinsLoadingState.value.pageSize !== undefined
    ) {
      return await loadTwins(
        {
          pageSize: twinsLoadingState.value.pageSize,
          page: twinsLoadingState.value.page,
        },
        twinsLoadingState.value.query,
      );
    }
  }

  // Get a single asset twin template
  const templateLoadingState = ref<LoadState<TwinResponse>>({
    ...stateInitial,
  });

  async function loadTemplate(id: string, reload = true) {
    if (
      !reload &&
      (templateLoadingState.value.loaded ||
        templateLoadingState.value.loading) &&
      templateLoadingState.value.result?.uuid === id
    ) {
      return;
    }
    templateLoadingState.value = { ...stateLoading };
    const response = await getTwin(id).catch((e) => {
      templateLoadingState.value = { ...stateError };
      throw e;
    });
    templateLoadingState.value = { ...stateLoaded, result: response.data };
    // NOTE: properties are sorted by date from the backend, we need sort by order
    response.data.properties?.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }

  // WARNING: Legacy singleton state. Do not use for new code: concurrent views
  // overwrite each other's result. Use twinLoadingStates with getTwinLoadingState.
  const twinLoadingState = ref<LoadState<TwinResponse>>({
    ...stateInitial,
  });

  // Cache twins by UUID so concurrent views, such as a ticket detail and preview
  // drawer, keep independent loading, error, and result state. New consumers must
  // access an entry through getTwinLoadingState and load it with loadTwinCached.
  const twinLoadingStates = ref<Map<string, LoadState<TwinResponse>>>(
    new Map(),
  );
  const twinLoadingRequests = new Map<string, number>();

  // Use this getter instead of reading the Map directly to get a stable initial
  // state before a twin has been loaded.
  function getTwinLoadingState(id: string): LoadState<TwinResponse> {
    return twinLoadingStates.value.get(id) ?? { ...stateInitial };
  }

  // Use this loader for new code. It only updates the requested UUID cache entry,
  // unlike the legacy loadTwin singleton loader.
  async function loadTwinCached(
    id: string,
    reload = true,
    loadInBackground = false,
  ): Promise<TwinResponse> {
    const current = twinLoadingStates.value.get(id);
    if (!reload && current?.loaded && current.result?.uuid === id) {
      return current.result;
    }

    const request = (twinLoadingRequests.get(id) ?? 0) + 1;
    twinLoadingRequests.set(id, request);
    if (!loadInBackground || current?.result?.uuid !== id) {
      twinLoadingStates.value.set(id, {
        ...current,
        ...stateLoading,
        result: current?.result,
      });
    }

    const response = await getTwin(id).catch((e) => {
      if (twinLoadingRequests.get(id) === request) {
        twinLoadingStates.value.set(id, {
          ...stateError,
          errorObject: e,
          result: current?.result,
        });
      }
      throw e;
    });
    // NOTE: properties are sorted by date from the backend, we need sort by order
    response.data.properties?.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    if (twinLoadingRequests.get(id) === request) {
      twinLoadingStates.value.set(id, {
        ...stateLoaded,
        result: response.data,
      });
    }
    return response.data;
  }

  // WARNING: Legacy singleton loader. Do not use for new code because loading a
  // second twin replaces twinLoadingState for every existing consumer.
  async function loadTwin(
    id: string,
    reload = true,
    loadInBackground = false,
  ): Promise<TwinResponse> {
    if (
      !reload &&
      twinLoadingState.value.loaded &&
      twinLoadingState.value.result?.uuid === id
    ) {
      return twinLoadingState.value.result;
    }
    if (id !== twinLoadingState.value.result?.uuid) {
      twinLoadingState.value = { ...stateLoading };
    } else if (!loadInBackground) {
      twinLoadingState.value = {
        ...stateLoading,
        result: twinLoadingState.value.result,
      };
    }
    const response = await getTwin(id).catch((e) => {
      twinLoadingState.value = { ...stateError, errorObject: e };
      throw e;
    });
    twinLoadingState.value = { ...stateLoaded, result: response.data };
    // NOTE: properties are sorted by date from the backend, we need sort by order
    twinLoadingState.value.result!.properties?.sort(
      (a, b) => (a.order ?? 0) - (b.order ?? 0),
    );
    return twinLoadingState.value.result!;
  }

  async function reloadTwin(id?: string, loadInBackground = false) {
    if (!id) {
      id = twinLoadingState.value?.result?.uuid;
    }
    if (id !== undefined && id === twinLoadingState.value?.result?.uuid) {
      await loadTwin(id, true, loadInBackground);
    }
    if (id !== undefined && twinLoadingStates.value.has(id)) {
      await loadTwinCached(id, true, loadInBackground);
    }
    // Check if twin is also in list of twins, if yes, reload
    if (twinsLoadingState.value?.result?.some((item) => item.uuid === id)) {
      await reloadTwins();
    }
    // Also do this for all cached table data
    Array.from(twinTables.value.keys()).forEach((key) => {
      const table = twinTables.value.get(key);
      if (table?.result?.some((item) => item.uuid === id)) {
        void reloadTwinTable(key);
      }
    });
  }

  // Replace an already-loaded twin with an authoritative response without
  // triggering additional requests or changing loading state.
  function replaceLoadedTwin(twin: TwinResponse) {
    twin.properties?.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

    if (twinLoadingState.value.result?.uuid === twin.uuid) {
      twinLoadingState.value = { ...twinLoadingState.value, result: twin };
    }

    const cachedTwin = twinLoadingStates.value.get(twin.uuid);
    if (cachedTwin?.result?.uuid === twin.uuid) {
      twinLoadingStates.value.set(twin.uuid, {
        ...cachedTwin,
        result: twin,
      });
    }

    if (
      twinsLoadingState.value.result?.some((item) => item.uuid === twin.uuid)
    ) {
      twinsLoadingState.value = {
        ...twinsLoadingState.value,
        result: twinsLoadingState.value.result.map((item) =>
          item.uuid === twin.uuid ? twin : item,
        ),
      };
    }

    twinTables.value.forEach((table, id) => {
      if (table.result?.some((item) => item.uuid === twin.uuid)) {
        twinTables.value.set(id, {
          ...table,
          result: table.result.map((item) =>
            item.uuid === twin.uuid ? twin : item,
          ),
        });
      }
    });
  }

  async function patchTemplateProperties(
    propertyId: string,
    request: {
      name?: string;
      description?: string;
      isUserRelevant?: boolean;
      constraint: PropertyConstraint;
      customId?: string;
    },
  ) {
    await patchProperty(propertyId, request);
    if (templateLoadingState.value.result) {
      await loadTemplate(templateLoadingState.value.result.uuid);
    }
  }

  // NOTE: the backend didn't support creating twins with properties, this function was used to do this and is now deprecated
  // @deprecated
  async function saveTwin(
    template: TwinResponse,
    propertyData: Array<{
      propertyUuid?: string;
      value: string | string[] | number;
    }>,
    comment?: string,
    longComment?: string,
  ) {
    // NOTE: properties here is using customId propertyData is using template id
    const properties = propertyData.flatMap((propData) => {
      const customId = (template.properties ?? []).find(
        (templateProp) => templateProp.uuid === propData.propertyUuid,
      )?.customId;
      return customId ? [{ property: customId, value: propData.value }] : [];
    });

    const twinRequest = {
      name: template.name,
      type: template.type,
      namespace: template.namespace,
      description: template.description,
      initial: template.initial,
      version: template.version,
      active: true,
      locked: false,

      isTemplate: false,
      templateUuid: template.uuid,
      properties,
      comment,
      longComment,
    };

    const twinResponse = await createTwinWithProperties(twinRequest);

    return twinResponse;
  }

  async function editTwin(asset: TwinResponse, payload: TwinPropertyValues) {
    await addTwinPropertyValues(asset.uuid, payload);
    await reloadTwin(asset.uuid, true);
  }

  // NOTE: though this is a patch, the backend currently requires all data to be set
  async function patchAssetTwin(id: string, query: TwinPatch) {
    const response = await patchTwin(id, query).then(() => {
      if (twinLoadingState.value.result?.uuid === id) {
        twinLoadingState.value.result = {
          ...twinLoadingState.value.result,
          ...query,
        };
      }
      const cached = twinLoadingStates.value.get(id);
      if (cached?.result?.uuid === id) {
        twinLoadingStates.value.set(id, {
          ...cached,
          result: {
            ...cached.result,
            ...query,
          },
        });
      }
    });
    return response;
  }

  async function patchAssetTwinTemplate(id: string, query: TwinPatch) {
    const response = await patchTwin(id, query).then(() => {
      if (templateLoadingState.value.result?.uuid === id) {
        templateLoadingState.value.result = {
          ...templateLoadingState.value.result,
          ...query,
        };
      }
    });
    return response;
  }

  async function deleteTwin(
    id: string,
    comment?: string,
    longComment?: string,
  ) {
    // Deleting an asset means setting active: false on the twin
    const response = await patchTwin(id, {
      active: false,
      comment,
      longComment,
    });
    void reloadTwins();
    return response;
  }

  // Property Constraints
  const propertyConstraintsLoadingState = ref<LoadState<PropertyConstraint[]>>({
    ...stateInitial,
    result: [],
  });

  async function loadPropertyConstraints() {
    propertyConstraintsLoadingState.value = {
      ...stateLoading,
      result: propertyConstraintsLoadingState.value.result,
    };
    const response = await getPropertyConstraints().catch((e) => {
      propertyConstraintsLoadingState.value = { ...stateError, result: [] };
      throw e;
    });
    propertyConstraintsLoadingState.value = {
      ...stateLoaded,
      result: response.data.result,
    };
    // propertyConstraintsLoadingState.value = {...stateLoaded, result: response.data.result}
  }

  // Value lists
  const valueListsLoadingState = ref<
    SearchResponseState<ValueListMetaData[], unknown>
  >({ ...stateInitial, result: [] });

  // NOTE: currently we do not use pagination
  async function loadValueLists(options?: { refresh?: boolean }) {
    if (
      options?.refresh === false &&
      (valueListsLoadingState.value.loaded ||
        valueListsLoadingState.value.loading)
    ) {
      return;
    }
    valueListsLoadingState.value = {
      ...valueListsLoadingState.value,
      ...stateLoading,
    };
    const response = await getValueListsMetaData().catch((error) => {
      valueListsLoadingState.value = {
        ...valueListsLoadingState.value,
        ...stateError,
      };
      throw error;
    });
    valueListsLoadingState.value = { ...stateLoaded, ...response.data };
  }

  async function updateValueList(
    id: string,
    valueList: Partial<ValueListMetaData>,
  ) {
    valueListsLoadingState.value = {
      ...valueListsLoadingState.value,
      ...stateLoading,
    };
    const response = await patchValuesList(id, valueList).catch((error) => {
      valueListsLoadingState.value = {
        ...valueListsLoadingState.value,
        ...stateError,
      };
      throw error;
    });
    valueListsLoadingState.value.result?.splice(
      valueListsLoadingState.value.result.findIndex((item) => item.uuid === id),
      1,
      response.data,
    );
    valueListsLoadingState.value = {
      ...valueListsLoadingState.value,
      ...stateLoaded,
    };
  }

  async function createValueList(valueList: ValueListMetaData) {
    valueListsLoadingState.value = {
      ...valueListsLoadingState.value,
      ...stateLoading,
    };
    const response = await postValueList(valueList);
    void loadValueLists();
    return response.data;
  }

  async function removeValueList(id: string) {
    valueListsLoadingState.value = {
      ...valueListsLoadingState.value,
      ...stateLoading,
    };
    const response = await deleteValueList(id);
    void loadValueLists();
    return response.data;
  }

  const valueListItemsLoadingState = ref<
    Map<string, SearchResponseState<ValueListItem[], unknown>>
  >(new Map());

  async function loadValueListItems(id: string, refresh = true) {
    if (!refresh && valueListItemsLoadingState.value.get(id)?.loaded) {
      return;
    }
    valueListItemsLoadingState.value.set(id, {
      ...valueListItemsLoadingState.value,
      ...stateLoading,
    });
    const response = await getValueListItems(id).catch((error) => {
      valueListItemsLoadingState.value.set(id, { ...stateError });
      throw error;
    });
    valueListItemsLoadingState.value.set(id, {
      ...stateLoaded,
      ...response.data,
    });
  }

  async function createValueListItem(valueListItem: ValueListItem) {
    const response = await addValueListItem(valueListItem);
    const items = valueListItemsLoadingState.value.get(
      valueListItem.valueListId,
    );
    items?.result?.push(response.data);
  }

  async function removeValueListItem(listId: string, id: string) {
    await deleteValueListItem(id);
    const items = valueListItemsLoadingState.value.get(listId);
    if (items?.result) {
      items.result.splice(
        items.result.findIndex((item) => item.uuid === id),
        1,
      );
    }
  }

  // Relations
  const relationsLoadingState = ref<
    SearchResponseState<TwinRelationResponse[], TwinRelationsQuery> & {
      twins: TwinResponse[];
    }
  >({ ...stateInitial, result: [], twins: [] });

  async function loadTwinRelations(query: TwinRelationsQuery) {
    relationsLoadingState.value = {
      ...relationsLoadingState.value,
      ...stateLoading,
      query,
    };
    const response = await getTwinRelations(query).catch((error) => {
      relationsLoadingState.value = {
        ...relationsLoadingState.value,
        ...stateError,
        query,
      };
      throw error;
    });
    relationsLoadingState.value = { ...stateLoaded, ...response.data, query };
  }

  async function addTwinRelation(request: TwinRelationRequest) {
    await postTwinRelation(request);
    if (relationsLoadingState.value.query) {
      void loadTwinRelations(
        relationsLoadingState.value.query as TwinRelationsQuery,
      );
    }
  }

  async function removeTwinRelation(id: string) {
    await deleteTwinRelation(id);
    void loadTwinRelations(
      relationsLoadingState.value.query as TwinRelationsQuery,
    );
  }

  const templateRelationsLoadingState = ref<LoadState<TwinTemplateRelations>>({
    ...stateInitial,
  });

  async function loadTemplateRelations(templateId: string) {
    templateRelationsLoadingState.value = {
      ...templateRelationsLoadingState.value,
      ...stateLoading,
    };
    const response = await getTwinTemplateRelations(templateId).catch(
      (error) => {
        templateRelationsLoadingState.value = {
          ...templateRelationsLoadingState.value,
          ...stateError,
        };
        throw error;
      },
    );
    templateRelationsLoadingState.value = {
      ...stateLoaded,
      result: response.data,
    };
  }

  // Maintain search queries on the list view
  const searchQueries = reactive<Record<string, string>>(
    JSON.parse(localStorage.getItem("twinFilterSearch") || "{}"),
  );

  watch(searchQueries, () => {
    localStorage.setItem("twinFilterSearch", JSON.stringify(searchQueries));
  });

  // Maintain filters of list views, primary keys are the templateId of the lit view, secondary keys are the customIds of input values
  const filters = reactive<Record<string, Record<string, string>>>(
    JSON.parse(localStorage.getItem("twinFilterCache") || "{}"),
  );

  watch(filters, () => {
    localStorage.setItem("twinFilterCache", JSON.stringify(filters));
  });

  return {
    templatesLoadingState,
    getTwinTemplates,
    twinTypes,
    templateByPropertyUuid,
    createTwinTemplate,
    createTemplateProperty,
    deleteTemplateProperty,
    twinsLoadingState,
    loadTwins,
    reloadTwins,
    twinLoadingState,
    loadTwin,
    reloadTwin,
    replaceLoadedTwin,
    twinLoadingStates,
    getTwinLoadingState,
    loadTwinCached,
    templateLoadingState,
    loadTemplate,
    saveTwin,
    editTwin,
    patchAssetTwin,
    deleteTwin,
    patchTemplateProperties,
    patchAssetTwinTemplate,
    propertyConstraintsLoadingState,
    loadPropertyConstraints,
    // Twin tables
    twinTables,
    loadTwinTable,
    mergedTwinTableData,
    // Value lists
    valueListsLoadingState,
    loadValueLists,
    removeValueList,
    createValueList,
    updateValueList,
    createValueListItem,
    removeValueListItem,
    valueListItemsLoadingState,
    loadValueListItems,
    // Relations
    relationsLoadingState,
    loadTwinRelations,
    addTwinRelation,
    removeTwinRelation,
    templateRelationsLoadingState,
    loadTemplateRelations,
    // search and filter caches
    searchQueries,
    filters,
  };
});
